import { z } from 'zod';

export const streamQuality = z.enum(['HIGH', 'LOW']);
export type StreamQuality = z.infer<typeof streamQuality>;

// Mirrors the Prisma `MediaProvider` enum: the streaming backend a patient's
// cameras are served from. `null` on a patient means "follow MEDIA_PROVIDER".
export const mediaProviderEnum = z.enum(['antmedia', 'mediamtx']);
export type MediaProviderName = z.infer<typeof mediaProviderEnum>;

export const updatePatientMediaProviderBody = z.object({
  mediaProvider: mediaProviderEnum.nullable(),
});
export type UpdatePatientMediaProviderBody = z.infer<typeof updatePatientMediaProviderBody>;

export const streamCameraIdParam = z.object({ cameraId: z.string().min(1) });
export type StreamCameraIdParam = z.infer<typeof streamCameraIdParam>;

export const streamLiveParams = z.object({
  cameraId: z.string().min(1),
  quality: streamQuality,
});
export type StreamLiveParams = z.infer<typeof streamLiveParams>;

export const streamCheckLiveParams = z.object({ streamId: z.string().min(1) });
export type StreamCheckLiveParams = z.infer<typeof streamCheckLiveParams>;

export const streamCheckLiveQuery = z.object({ token: z.string().min(1) });
export type StreamCheckLiveQuery = z.infer<typeof streamCheckLiveQuery>;

export const streamNvrIdParam = z.object({ nvrId: z.string().min(1) });
export type StreamNvrIdParam = z.infer<typeof streamNvrIdParam>;

export const streamPatientIdParam = z.object({ patientId: z.string().min(1) });
export type StreamPatientIdParam = z.infer<typeof streamPatientIdParam>;

export const updateStreamPreferencesBody = z.object({
  patientIds: z.array(z.uuid()).min(0),
});
export type UpdateStreamPreferencesBody = z.infer<typeof updateStreamPreferencesBody>;

export const matchingPatient = z.object({
  id: z.string(),
  name: z.string(),
  inMonitoringHours: z.boolean(),
  selected: z.boolean(),
});
export type MatchingPatient = z.infer<typeof matchingPatient>;

export const streamPreferencesResponse = z.object({
  patients: z.array(matchingPatient),
});
export type StreamPreferencesResponse = z.infer<typeof streamPreferencesResponse>;

export const streamDataResponse = z.object({
  streamId: z.string(),
  token: z.string(),
  hlsUrl: z.string().optional(),
  vodUrl: z.string().optional(),
});
export type StreamDataResponse = z.infer<typeof streamDataResponse>;

// `missing` = absent but expected on a reachable slot; `unknown` = expected but
// the owning slot was unreachable, so presence is indeterminate; `error` = absent,
// not expected, with a recent start failure recorded.
export const mediaStatusStreamState = z.enum(['online', 'pending', 'missing', 'error', 'unknown']);
export type MediaStatusStreamState = z.infer<typeof mediaStatusStreamState>;

export const mediaStatusSlot = z.object({
  name: z.string(),
  reachable: z.boolean(),
  version: z.string().nullable(),
  pathCount: z.number().int().nullable(),
});
export type MediaStatusSlot = z.infer<typeof mediaStatusSlot>;

export const mediaStatusStream = z.object({
  streamId: z.string(),
  provider: mediaProviderEnum,
  cameraId: z.string(),
  cameraName: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  quality: streamQuality.nullable(),
  slot: z.string().nullable(),
  state: mediaStatusStreamState,
  expected: z.boolean(),
  onlineTime: z.string().nullable(),
  pendingSince: z.string().nullable(),
  viewers: z.number().int(),
  bytesPerMinute: z.number().nullable(),
  bytes24h: z.number().nullable(),
  drops24h: z.number().int(),
  lastError: z.object({ message: z.string(), at: z.string() }).nullable(),
});
export type MediaStatusStream = z.infer<typeof mediaStatusStream>;

export const mediaStatusResponse = z.object({
  available: z.boolean(),
  slots: z.array(mediaStatusSlot),
  streams: z.array(mediaStatusStream),
});
export type MediaStatusResponse = z.infer<typeof mediaStatusResponse>;

export const mediaHookKind = z.enum(['ready', 'notready', 'read', 'unread']);
export type MediaHookKind = z.infer<typeof mediaHookKind>;

export const mediaHookParams = z.object({
  slotId: z.string().min(1),
  kind: mediaHookKind,
});
export type MediaHookParams = z.infer<typeof mediaHookParams>;

export const mediaHookBody = z.object({
  path: z.string().min(1),
  readerType: z.string().optional(),
  readerId: z.string().optional(),
});
export type MediaHookBody = z.infer<typeof mediaHookBody>;

// Months are UTC calendar months, matching the UTC hour bucketing of usage rows.
const monthPattern = /^\d{4}-(0[1-9]|1[0-2])$/;

export const mediaUsageGranularity = z.enum(['hour', 'day', 'month', 'all']);
export type MediaUsageGranularity = z.infer<typeof mediaUsageGranularity>;

// `hour`/`day` aggregate within the selected month (default: current month);
// `month` returns one row per month over all data; `all` returns grand totals.
export const getMediaUsageQuery = z.object({
  granularity: mediaUsageGranularity.default('day'),
  month: z.string().regex(monthPattern).optional(),
  patientId: z.uuid().optional(),
});
export type GetMediaUsageQuery = z.infer<typeof getMediaUsageQuery>;

export const mediaUsageItem = z.object({
  period: z.string(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  quality: streamQuality,
  bytes: z.number(),
});
export type MediaUsageItem = z.infer<typeof mediaUsageItem>;

export const mediaUsageResponse = z.object({
  items: z.array(mediaUsageItem),
  totals: z.object({
    bytes: z.number(),
    highBytes: z.number(),
    lowBytes: z.number(),
  }),
});
export type MediaUsageResponse = z.infer<typeof mediaUsageResponse>;

export const getMediaDowntimeQuery = z.object({
  month: z.string().regex(monthPattern).optional(),
  patientId: z.uuid().optional(),
});
export type GetMediaDowntimeQuery = z.infer<typeof getMediaDowntimeQuery>;

export const mediaDowntimeSource = z.enum(['hook', 'watchdog']);
export type MediaDowntimeSource = z.infer<typeof mediaDowntimeSource>;

export const mediaDowntimeEndReason = z.enum(['recovered', 'stopped']);
export type MediaDowntimeEndReason = z.infer<typeof mediaDowntimeEndReason>;

// durationMs/monitoringMs are clamped to the selected month window so monthly
// totals stay additive; startedAt/endedAt are the raw timestamps for display.
export const mediaDowntimeEvent = z.object({
  id: z.string(),
  streamId: z.string(),
  cameraId: z.string(),
  cameraName: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  quality: streamQuality,
  slot: z.string().nullable(),
  source: mediaDowntimeSource,
  endReason: mediaDowntimeEndReason.nullable(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationMs: z.number(),
  monitoringMs: z.number(),
  duringMonitoring: z.boolean(),
});
export type MediaDowntimeEvent = z.infer<typeof mediaDowntimeEvent>;

// Summary downtimeMs/monitoringDowntimeMs are the merged wall-clock union across
// all returned events — simultaneous outages on several streams count once, so
// the totals never exceed the window length. Per-stream durations live on events.
export const mediaDowntimeResponse = z.object({
  events: z.array(mediaDowntimeEvent),
  summary: z.object({
    drops: z.number().int(),
    dropsDuringMonitoring: z.number().int(),
    downtimeMs: z.number(),
    monitoringDowntimeMs: z.number(),
  }),
});
export type MediaDowntimeResponse = z.infer<typeof mediaDowntimeResponse>;
